import { computed, markRaw, ref, type Component, type ComputedRef } from 'vue';
import { useState } from '#imports';
import type {
  ComponentProps,
  OpenedOverlay,
  OverlayInstance,
  OverlayOptions,
} from './overlay.types';

const generateId = (): string =>
  `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

/**
 * Programmatic modals, rendered by the `OverlayProvider` that `App` mounts.
 * State lives in Nuxt's `useState` (per request) or its plain-Vue stand-in, so
 * it can be called from components, composables and router guards alike. The
 * mounted component receives its props plus `open`, and must forward the
 * modal's `update:open` and `after:leave`; emitting `close(value)` resolves the
 * `result` promise of the `open()` call.
 */
export const useOverlay = () => {
  const overlays = useState<OverlayInstance[]>(
    'exfluential-ui.overlays',
    () => [],
  );

  const find = (id: string): OverlayInstance | undefined =>
    overlays.value.find((item: OverlayInstance) => item.id === id);

  const replace = (id: string, patch: Partial<OverlayInstance>): void => {
    overlays.value = overlays.value.map((item: OverlayInstance) =>
      item.id === id ? { ...item, ...patch } : item,
    );
  };

  const close = (id: string, value?: unknown): void => {
    const instance = find(id);
    if (!instance?.open) {
      return;
    }
    replace(id, { open: false });
    instance.resolve(value);
  };

  const unmount = (id: string): void => {
    const instance = find(id);
    if (!instance || instance.open) {
      return;
    }
    if (instance.destroyOnClose) {
      overlays.value = overlays.value.filter(
        (item: OverlayInstance) => item.id !== id,
      );
    }
  };

  const clear = (): void => {
    overlays.value = [];
  };

  const create = <T extends Component, R = unknown>(
    component: T,
    options: OverlayOptions<T> = {},
  ) => {
    const destroyOnClose = options.destroyOnClose ?? true;
    const currentId = ref<string>();

    const patch = (id: string, props: Partial<ComponentProps<T>>): void => {
      const instance = find(id);
      if (instance) {
        replace(id, { props: { ...instance.props, ...props } });
      }
    };

    const open = (
      props: Partial<ComponentProps<T>> = {},
    ): OpenedOverlay<T, R> => {
      const existing =
        currentId.value === undefined ? undefined : find(currentId.value);
      const id = existing?.id ?? generateId();
      let resolve!: (value: unknown) => void;
      const result = new Promise<unknown>((done) => {
        resolve = done;
      });
      const instance: OverlayInstance = {
        id,
        component: markRaw(component),
        props: { ...options.props, ...props },
        open: true,
        destroyOnClose,
        resolve,
      };
      overlays.value = existing
        ? overlays.value.map((item: OverlayInstance) =>
            item.id === id ? instance : item,
          )
        : [...overlays.value, instance];
      currentId.value = id;
      return {
        id,
        result: result as Promise<R | undefined>,
        close: (value?: R) => close(id, value),
        patch: (next) => patch(id, next),
      };
    };

    const isOpen: ComputedRef<boolean> = computed(() =>
      currentId.value === undefined
        ? false
        : (find(currentId.value)?.open ?? false),
    );

    return {
      open,
      close: (value?: R): void => {
        if (currentId.value !== undefined) {
          close(currentId.value, value);
        }
      },
      patch: (props: Partial<ComponentProps<T>>): void => {
        if (currentId.value !== undefined) {
          patch(currentId.value, props);
        }
      },
      isOpen,
    };
  };

  return { overlays, create, close, unmount, clear };
};
